import { useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImagePlus, X } from "lucide-react";

export interface PhotoEntry {
  url: string;
  description: string;
}

interface PhotoUploaderProps {
  photos: PhotoEntry[];
  onChange: (photos: PhotoEntry[]) => void;
}

const PhotoUploader = ({ photos, onChange }: PhotoUploaderProps) => {
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const newPhotos: PhotoEntry[] = Array.from(files).map((file) => ({
      url: URL.createObjectURL(file),
      description: "",
    }));
    onChange([...photos, ...newPhotos]);
  };

  const removePhoto = (index: number) => {
    onChange(photos.filter((_, i) => i !== index));
  };

  const updateDescription = (index: number, description: string) => {
    const updated = [...photos];
    updated[index] = { ...updated[index], description };
    onChange(updated);
  };

  return (
    <Card className="border-border bg-card shadow-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-foreground">
          <ImagePlus className="h-5 w-5 text-primary" />
          Site Photos
        </CardTitle>
      </CardHeader>
      <CardContent>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
        
        <div className="space-y-3">
          {photos.map((photo, i) => (
            <div key={i} className="rounded-lg border border-border overflow-hidden">
              <div className="relative group aspect-video">
                <img src={photo.url} alt={`Site photo ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              <div className="p-2">
                <Input
                  value={photo.description}
                  onChange={(e) => updateDescription(i, e.target.value)}
                  placeholder={`Describe this photo (e.g., "Main entrance, north side")`}
                  className="bg-secondary border-border text-foreground text-sm"
                />
              </div>
            </div>
          ))}
          <button
            onClick={() => fileRef.current?.click()}
            className="w-full aspect-video rounded-lg border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <ImagePlus className="h-6 w-6" />
            <span className="text-xs">Add Photo</span>
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PhotoUploader;
